/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import {
  AlertCircle,
  LockIcon,
  Eye,
  EyeOff,
  Clock,
  XCircle,
  AlertTriangle,
  Check,
} from "lucide-react";
import { getApi, postApi } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";

import { useTranslation } from "react-i18next";
import AuthService from "../services/AuthService";
import SignUpService from "../services/SignUpService";
import { getEnvLocalizedUrl } from "@/shared/utils/urlLocalization";
import { analyticsService } from "@/shared/services/analytics";
import { useAuth } from "../state/AuthContext";
import { Icons } from "@/shared/components/icons/icons";
import { logger } from "@/infrastructure/lib/logger";

// Interface para o resultado da validação do token
interface ValidatedInvite {
  id: string;
  email: string;
  userName: string | null;
  role: string | null;
  organizationId: string | null;
  // Legacy validateInviteToken nunca retornou estes campos (ver
  // apps/api/src/repositories/user-register.repository.ts:205-215). v2 mantém
  // parity — campos opcionais por compat com o tipo histórico.
  token?: string;
  expiresAt?: string;
  isValid: boolean; // Indica se o token é válido
  isExpired: boolean; // Indica se o token está expirado
  matchesUser?: boolean; // Indica se o token corresponde ao usuário
  // O email do convite já tem conta no Prodgy (AB#84509). Quem decide é o backend:
  // esta tela é pública e o estado de sessão do navegador é instável no momento do
  // clique (sessão carregando, outro navegador, outra conta logada).
  accountExists?: boolean;
}

// Função para criar o schema de validação com traduções dinâmicas
const createSetupPasswordSchema = (t: (key: string) => string) => {
  return z
    .object({
      password: z
        .string()
        .min(8, t("register.validation.passwordMinLength"))
        .regex(/[A-Z]/, t("register.passwordRequirements.upperCase"))
        .regex(/[a-z]/, t("register.passwordRequirements.lowerCase"))
        .regex(/[0-9]/, t("register.passwordRequirements.number"))
        .regex(/[^A-Za-z0-9]/, t("register.passwordRequirements.specialChar")),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("register.validation.passwordsMustMatch"),
      path: ["confirmPassword"],
    });
};

export default function SetupPassword() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [inviteData, setInviteData] = useState<ValidatedInvite | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const { signIn } = useAuth();

  /**
   * O email do convite já tem conta? Quem decide é o backend (AB#84509).
   *
   * Quando tem, esta tela NÃO aceita o convite: apenas encaminha para o login. Aceitar
   * por link é aceitar por posse do email — quem tiver acesso à caixa entra na
   * organização no lugar da pessoa. O aceite acontece autenticado, nas notificações,
   * amarrado à identidade verificada.
   *
   * O email novo já nem manda para cá; esta tela cobre os links antigos, ainda válidos.
   */
  const inviteeHasAccount = inviteData?.accountExists === true;

  // Check if social sign-in is disabled via environment variable
  const isSocialSignInDisabled =
    (window as any).ENV?.VITE_DISABLE_SOCIAL_SIGNIN &&
    (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN !==
      "%%VITE_DISABLE_SOCIAL_SIGNIN%%"
      ? (window as any).ENV.VITE_DISABLE_SOCIAL_SIGNIN === "true"
      : import.meta.env.VITE_DISABLE_SOCIAL_SIGNIN === "true";

  // Limpar qualquer sessão existente ao carregar a página - silenciosamente
  useEffect(() => {
    const clearSessionSilently = async () => {
      try {
        // 1. Fazer logout completo no backend através do AuthService
        // Esta chamada vai invalidar os cookies HTTP-only e a sessão no servidor
        localStorage.removeItem("prodgy-auth-state");

        // 3. Limpeza de sessão do Supabase removida - migrado para backend

        // 4. Limpar quaisquer tokens JWT armazenados em cookies
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie;
          document.cookie =
            name + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;";
        }
      } catch (error) {
        logger.error("[SetupPassword] Erro ao limpar sessão anterior", error);
      }
    };

    clearSessionSilently();
  }, []);

  // Obter os parâmetros da URL
  const params = new URLSearchParams(location.search);
  const inviteToken = params.get("invite_token");
  // userId não será mais obtido da URL, pois agora está contido no token JWT

  // Detectar o idioma do navegador (implementado via useTranslation)
  // O react-i18next já faz a detecção automática do idioma do navegador
  // e configura o idioma correto, então não precisamos fazer isso manualmente

  // Criar schema de validação com as traduções corretas
  const setupPasswordSchema = createSetupPasswordSchema(t);

  // Formulário para configuração de senha - usamos o tipo criado pela função de schema
  const form = useForm<z.infer<ReturnType<typeof createSetupPasswordSchema>>>({
    resolver: zodResolver(setupPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const passwordValue = form.watch("password") ?? "";
  const confirmPasswordValue = form.watch("confirmPassword") ?? "";

  const hasMinLength = passwordValue.length >= 8;
  const hasUpperCase = /[A-Z]/.test(passwordValue);
  const hasLowerCase = /[a-z]/.test(passwordValue);
  const hasNumber = /[0-9]/.test(passwordValue);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(passwordValue);
  const passwordsMatch =
    confirmPasswordValue.length > 0 && passwordValue === confirmPasswordValue;

  const passwordRequirements = [
    {
      label: t("register.passwordRequirements.minChars"),
      isValid: hasMinLength,
    },
    {
      label: t("register.passwordRequirements.upperCase"),
      isValid: hasUpperCase,
    },
    {
      label: t("register.passwordRequirements.lowerCase"),
      isValid: hasLowerCase,
    },
    { label: t("register.passwordRequirements.number"), isValid: hasNumber },
    {
      label: t("register.passwordRequirements.specialChar"),
      isValid: hasSpecialChar,
    },
    {
      label: t("register.passwordRequirements.match"),
      isValid: passwordsMatch,
    },
  ];

  // Validar token na inicialização
  useEffect(() => {
    const validateToken = async () => {
      if (!inviteToken) {
        setError(t("register.errors.invalidInvite"));
        setValidating(false);
        setLoading(false);
        return;
      }

      try {
        const response = await getApi<ValidatedInvite>(
          `${API_CONFIG.API_ROOT_URL}/invites/validate-token/${inviteToken}`,
        ).then(
          (flat) =>
            ({ success: true, data: flat }) as {
              success: boolean;
              data: ValidatedInvite;
              message?: string;
            },
        );

        if (response.success && response.data) {
          // Se o token é válido, verificamos se não está expirado ou já foi usado
          if (response.data.isValid) {
            setInviteData(response.data);

            // Track password setup started
            analyticsService.trackEvent("password_setup_started", {});
          } else if (response.data.isExpired) {
            // Token expirado
            setError(
              t("register.errors.tokenExpired") ||
                "O link de convite expirou. Por favor, solicite um novo convite.",
            );
            setInviteData(null);
          } else {
            // Token já utilizado ou status não é 'pending'
            setError(
              t("register.errors.tokenUsed") ||
                "Este link de convite já foi utilizado ou foi cancelado.",
            );
            setInviteData(null);
          }
        } else {
          // Quando o token é inválido (não encontrado ou inválido)
          setError(
            response.message ||
              t("register.errors.invalidToken") ||
              "Link de convite inválido. Verifique se o link está correto.",
          );
          setInviteData(null);
        }
      } catch (error: any) {
        logger.error("[SetupPassword] Erro ao validar token", error);

        // Capturar mensagens de erro específicas do backend
        const errorMessage = error.response?.data?.message;
        if (errorMessage?.includes("expirado")) {
          setError(
            t("register.errors.tokenExpired") ||
              "O link de convite expirou. Por favor, solicite um novo convite.",
          );
        } else if (errorMessage?.includes("utilizado")) {
          setError(
            t("register.errors.tokenUsed") ||
              "Este link de convite já foi utilizado ou foi cancelado.",
          );
        } else {
          setError(
            errorMessage ||
              t("register.errors.validationError") ||
              "Ocorreu um erro ao validar o link de convite.",
          );
        }

        setInviteData(null); // Garante que não exibiremos o formulário
      } finally {
        setValidating(false);
        setLoading(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  // Função para finalizar o cadastro
  const onSubmit = async (
    values: z.infer<ReturnType<typeof createSetupPasswordSchema>>,
  ) => {
    if (!inviteData || !inviteData.email || !inviteToken || !inviteData.id) {
      setError(t("register.errors.invalidInvite"));
      return;
    }

    setSubmitting(true);
    setError(null);
    setPasswordError(null);

    try {
      // 1. Atualizar a senha do usuário existente usando o novo endpoint
      let setupResponse: {
        success: boolean;
        message?: string;
        errorCode?: string;
      };
      const payload: Record<string, unknown> = {
        userId: inviteData.id,
        inviteToken: inviteToken,
        password: values.password,
      };
      // v2 valida avatarUrl como URL — null quebra o validator. Só envia se truthy.

      const url = `${API_CONFIG.API_ROOT_URL}/invites/setup-password`;

      try {
        const flat = await postApi<{ userId?: string; tokens?: unknown }>(
          url,
          payload,
        );
        setupResponse = { success: !!flat.userId };
      } catch (fetchError: any) {
        if (fetchError.data) {
          setupResponse = fetchError.data;
        } else {
          throw fetchError;
        }
      }

      if (!setupResponse.success) {
        let errorMessage =
          setupResponse.message || t("register.errors.passwordSetupFailed");

        if (setupResponse.errorCode) {
          const errorKey = `register.errors.${setupResponse.errorCode}`;
          const translatedMessage = t(errorKey);

          if (translatedMessage !== errorKey) {
            errorMessage = translatedMessage;
          }
        }

        setPasswordError(errorMessage);
        return;
      }

      // 2. Fazer login automático utilizando o AuthService
      try {
        const authServiceInstance = AuthService.getInstance();
        const loginResponse = await authServiceInstance.login(
          inviteData.email,
          values.password,
        );

        if (!loginResponse.success || !loginResponse.data?.user) {
          logger.error(
            t("register.errors.autoLoginFailed"),
            loginResponse.message,
          );
          throw new Error(
            loginResponse.message || t("register.errors.autoLoginFailed"),
          );
        }

        // Garantir que tokens foram salvos (fallback para aba anônima)
        const tokens =
          (loginResponse.data as any)?.tokens ||
          (loginResponse.data as any)?.enhancedToken ||
          (loginResponse as any)?.tokens;

        if (tokens?.accessToken && tokens?.refreshToken) {
          localStorage.setItem("access_token", tokens.accessToken);
          localStorage.setItem("refresh_token", tokens.refreshToken);
        }
      } catch (loginError) {
        logger.error("[SetupPassword] Erro no login automático", loginError);
        throw loginError;
      }

      // 3. Sucesso!
      setSuccess(true);

      // Track password setup completed
      analyticsService.trackEvent("password_setup_completed", {});

      // 4. Aguardar um pouco para garantir que os cookies sejam definidos
      await new Promise((resolve) => setTimeout(resolve, 500));

      // 5. Verificar status de onboarding
      let redirectUrl = "/";
      try {
        const onboardingResponse = await SignUpService.checkOnboardingStatus();

        if (
          onboardingResponse.success &&
          onboardingResponse.data?.onboarding_required
        ) {
          redirectUrl = "/onboarding";
        }
      } catch (onboardingError) {
        logger.error(
          "[SetupPassword] Erro ao verificar status de onboarding",
          onboardingError,
        );
        // Em caso de erro, continua com redirecionamento padrão
      }

      // 6. Redirecionar diretamente após mostrar a tela de sucesso
      // Salvar no sessionStorage que é um primeiro login
      sessionStorage.setItem("firstLogin", "true");

      // Mostrar tela de sucesso brevemente e redirecionar
      // (A página de onboarding terá seu próprio carregamento)
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1500);
    } catch (error: any) {
      logger.error(t("register.errors.registrationProcessError"), error);
      const errorMessage =
        error.message || t("register.errors.registrationError");
      setError(errorMessage);

      // Track password setup failed
      analyticsService.trackEvent("password_setup_failed", {
        error_message: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialSignIn = async (provider: "google" | "azure") => {
    if (!inviteToken) return;

    const setLoaderFn =
      provider === "google" ? setIsGoogleLoading : setIsMicrosoftLoading;
    setLoaderFn(true);

    // Salvar invite_token em localStorage (SEMPRE persiste independente de navegação cross-origin)
    // NÃO modificar a redirect URL — GoTrue rejeita URLs com query params não cadastradas
    localStorage.setItem("prodgy_pending_invite_token", inviteToken);
    localStorage.setItem("prodgy_pending_invite_ts", Date.now().toString());
    sessionStorage.setItem("invite_token", inviteToken);

    try {
      await signIn(provider);
    } catch (error: any) {
      logger.error(`[SetupPassword] ${provider} sign in error`, {
        error: error.message,
      });
      setLoaderFn(false);
      localStorage.removeItem("prodgy_pending_invite_token");
      localStorage.removeItem("prodgy_pending_invite_ts");
      sessionStorage.removeItem("invite_token");
    }
  };

  // Renderização condicionall baseada no estado
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="flex flex-col items-center justify-center space-y-4">
          <LoadingSpinner className="h-10 w-10 text-primary" size="md" />
          <p className="text-lg font-medium">{t("register.checking")}</p>
        </div>
      </div>
    );
  }

  // Determinar o tipo de erro para exibição visual apropriada
  const getErrorDetails = () => {
    if (!error)
      return {
        title: t("register.errors.title"),
        description: t("register.errors.validationTitle"),
        icon: <AlertCircle className="h-16 w-16 text-red-500" />,
        colorClass: "bg-red-100",
        iconClass: "text-red-500",
        message: t("register.errors.invalidInvite"),
      };

    if (error.includes("expirou") || error.includes("expired")) {
      return {
        title: t("register.errors.expiredTitle"),
        description: t("register.errors.expiredDescription"),
        icon: <Clock className="h-16 w-16 text-amber-500" />,
        colorClass: "bg-amber-100",
        iconClass: "text-amber-500",
        message: error,
      };
    } else if (
      error.includes("utilizado") ||
      error.includes("used") ||
      error.includes("cancelado")
    ) {
      return {
        title: t("register.errors.usedTitle"),
        description: t("register.errors.usedDescription"),
        icon: <XCircle className="h-16 w-16 text-red-500" />,
        colorClass: "bg-red-100",
        iconClass: "text-red-500",
        message: error,
      };
    } else {
      return {
        title: t("register.errors.invalidTitle"),
        description: t("register.errors.invalidDescription"),
        icon: <AlertTriangle className="h-16 w-16 text-orange-500" />,
        colorClass: "bg-orange-100",
        iconClass: "text-orange-500",
        message: error,
      };
    }
  };

  // Se houver erro na validação do convite ou o token for inválido
  // Nota: garantimos que tanto erro quanto falta de dados de convite válidos bloqueiam o formulário
  if ((error || !inviteData) && validating === false) {
    const errorDetails = getErrorDetails();

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img
              src="/assets/logo.png"
              alt="Prodgy Logo"
              className="h-16 w-auto mb-6"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {errorDetails.title}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {errorDetails.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center text-center py-6">
              <div
                className={`mb-6 ${errorDetails.colorClass} p-4 rounded-full`}
              >
                {errorDetails.icon}
              </div>
              <p className="text-muted-foreground text-center mb-4">
                {errorDetails.message}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("register.errors.contactAdmin") ||
                  "Se você precisar de ajuda, entre em contato com o administrador do sistema."}
              </p>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => (window.location.href = "/login")}
              >
                {t("register.buttons.goToLogin")}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Powered by footer */}
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("common.poweredBy")}{" "}
            <a
              href="https://programmers.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-muted-foreground/80 transition-colors"
            >
              {t("common.companyName")}
            </a>
            {t("common.trademark")}
          </span>
        </div>
      </div>
    );
  }

  // Se o registro foi bem-sucedido
  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="flex flex-col items-center justify-center space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img
              src="/assets/logo.png"
              alt="Prodgy Logo"
              className="h-16 w-auto"
            />
          </div>
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <div className="space-y-3">
              <h2 className="text-2xl font-medium tracking-tight">
                {t("register.success.title")}
              </h2>
              <p className="text-muted-foreground">
                {t("register.success.description")}
              </p>
            </div>
            <LoadingSpinner size="lg" />
            <p className="text-sm text-muted-foreground">
              {t("register.success.message")}
            </p>
          </div>
        </div>

        {/* Powered by footer */}
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("common.poweredBy")}{" "}
            <a
              href="https://programmers.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-muted-foreground/80 transition-colors"
            >
              {t("common.companyName")}
            </a>
            {t("common.trademark")}
          </span>
        </div>
      </div>
    );
  }

  // Já tem conta: pedir senha de novo trataria quem já usa o Prodgy como cadastro
  // novo. Encaminha para o login — o convite espera nas notificações.
  if (inviteData?.email && inviteeHasAccount) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img
              src="/assets/logo.png"
              alt="Prodgy Logo"
              className="h-16 w-auto"
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>
                {t(
                  "register.joinOrganization.title",
                  "Você já tem conta no Prodgy",
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  "register.joinOrganization.signInToAccept",
                  "Entre com a conta deste email e o convite estará esperando nas notificações, no sino do topo da tela. Seus acessos atuais continuam como estão.",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {inviteData.email}
              </p>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                onClick={() => {
                  window.location.href = "/login";
                }}
              >
                {t("register.joinOrganization.signIn", "Entrar no Prodgy")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Formulário de setup de senha - só exibe se tiver dados de convite válidos
  // Esta é uma verificação adicional de segurança
  if (!inviteData || !inviteData.email) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img
              src="/assets/logo.png"
              alt="Prodgy Logo"
              className="h-16 w-auto mb-6"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("register.errors.title")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("register.errors.validationTitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("common.error")}</AlertTitle>
                <AlertDescription>
                  {error || t("register.errors.invalidInvite")}
                </AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => (window.location.href = "/login")}
              >
                {t("register.buttons.goToLogin")}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("common.poweredBy")}{" "}
            <a
              href="https://programmers.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-muted-foreground/80 transition-colors"
            >
              {t("common.companyName")}
            </a>
            {t("common.trademark")}
          </span>
        </div>
      </div>
    );
  }

  // Formulário de setup de senha
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="flex justify-center">
          <img
            src="/assets/logo.png"
            alt="Prodgy Logo"
            className="h-16 w-auto mb-6"
          />
        </div>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-3 pb-8">
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("register.welcomeTitle")}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {t("register.welcomeDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("common.error")}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {passwordError && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>
                  {t("register.errors.passwordErrorTitle")}
                </AlertTitle>
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="mb-4">
                  <FormLabel>{t("register.userInfo.email")}</FormLabel>
                  <Input
                    type="email"
                    value={inviteData?.email || ""}
                    disabled
                    className="bg-muted/50 h-11"
                  />
                </div>

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("register.formFields.password")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            {...field}
                            className="h-11 pl-10 pr-10 remove-password-toggle"
                            autoComplete="off"
                          />
                          <LockIcon className="h-5 w-5 absolute left-3 top-3 text-muted-foreground" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1 h-9 w-9 p-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="sr-only">
                              {showPassword
                                ? t("auth.hidePassword")
                                : t("auth.showPassword")}
                            </span>
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("register.formFields.confirmPassword")}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            {...field}
                            onBlur={(e) => {
                              field.onBlur();
                              if (e.target.value.length > 0) {
                                setConfirmPasswordTouched(true);
                              }
                            }}
                            onChange={(e) => {
                              field.onChange(e);
                              if (
                                confirmPasswordTouched &&
                                e.target.value === passwordValue
                              ) {
                                setConfirmPasswordTouched(false);
                              }
                            }}
                            className={`h-11 pl-10 pr-10 remove-password-toggle ${confirmPasswordTouched && !passwordsMatch && confirmPasswordValue.length > 0 ? "border-destructive focus-visible:ring-destructive" : ""}`}
                            autoComplete="off"
                          />
                          <LockIcon className="h-5 w-5 absolute left-3 top-3 text-muted-foreground" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1 h-9 w-9 p-0"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="sr-only">
                              {showConfirmPassword
                                ? t("auth.hidePassword")
                                : t("auth.showPassword")}
                            </span>
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="bg-primary/10 p-4 rounded-lg flex items-center space-x-3">
                  <div className="bg-primary/20 p-2 rounded-full">
                    <LockIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">
                      {t("register.passwordRequirements.title")}
                    </p>
                    <ul className="text-xs space-y-1">
                      {passwordRequirements.map(({ label, isValid }) => (
                        <li key={label} className="flex items-center gap-2">
                          {isValid ? (
                            <Check className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                          )}
                          <span
                            className={
                              isValid
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }
                          >
                            {label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full h-11 font-normal relative"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner
                          className="h-5 w-5 text-white"
                          size="sm"
                        />
                      </div>
                    ) : (
                      t("register.buttons.createAccount")
                    )}
                  </Button>
                </div>

                {!isSocialSignInDisabled && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          {t("auth.signup.orContinueWith")}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={() => handleSocialSignIn("google")}
                        disabled={
                          submitting || isGoogleLoading || isMicrosoftLoading
                        }
                        type="button"
                      >
                        {isGoogleLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.google className="mr-2 h-5 w-5" />
                            {t("auth.signup.continueWithGoogle")}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full font-normal h-11"
                        onClick={() => handleSocialSignIn("azure")}
                        disabled={
                          submitting || isGoogleLoading || isMicrosoftLoading
                        }
                        type="button"
                      >
                        {isMicrosoftLoading ? (
                          <LoadingSpinner className="h-5 w-5" size="sm" />
                        ) : (
                          <>
                            <Icons.microsoft className="mr-2 h-5 w-5" />
                            {t("auth.signup.continueWithMicrosoft")}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                <div className="pt-4 text-xs text-center">
                  <p className="text-muted-foreground">
                    {t("register.agreeTermsStart")}{" "}
                    <a
                      href={getEnvLocalizedUrl(
                        "VITE_PRODGY_TERMS_URL",
                        i18n.language,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t("register.termsOfService")}
                    </a>{" "}
                    {t("register.agreeTermsMiddle")}{" "}
                    <a
                      href={getEnvLocalizedUrl(
                        "VITE_PRODGY_PRIVACY_URL",
                        i18n.language,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t("register.privacyPolicy")}
                    </a>
                    {t("register.agreeTermsEnd")}
                  </p>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Powered by footer */}
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("common.poweredBy")}{" "}
          <a
            href="https://programmers.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-muted-foreground/80 transition-colors"
          >
            {t("common.companyName")}
          </a>
          {t("common.trademark")}
        </span>
      </div>
      <style>
        {`
          .remove-password-toggle::-ms-reveal,
          .remove-password-toggle::-ms-clear,
          .remove-password-toggle::-webkit-credentials-auto-fill-button,
          .remove-password-toggle::-webkit-contacts-auto-fill-button {
            display: none !important;
            visibility: hidden;
            pointer-events: none;
          }
        `}
      </style>
    </div>
  );
}
